/**
 * DataTable — Virtualized, accessible table component for the design system.
 *
 * Architecture:
 * - Split DOM: fixed left pane (pinned columns) + scrollable right pane
 * - @tanstack/react-virtual for row virtualization with variable heights
 * - Manual ARIA attributes for grid semantics and accessibility
 * - measureElement callback for @tanstack/react-virtual row height measurement
 * - Sticky header row via CSS position:sticky
 * - Selection via checkbox column with "select all" support
 * - Full keyboard navigation with roving tabindex
 * - SortableHeader for click-to-sort column headers
 * - RowContextMenu for right-click / long-press / keyboard context actions
 */

import { DndContext, DragOverlay, useDroppable } from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import type { Selection } from '@react-types/shared';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUpRight, DotsHorizontal } from '@untitledui/icons';
import type { ReactNode } from 'react';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SortDescriptor } from 'react-aria-components';
import {
  Button as AriaButton,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger as AriaMenuTrigger,
  Separator as AriaSeparator,
} from 'react-aria-components';
import { toast } from 'sonner';

import { NonModalAriaPopover } from '@/lib/base/overlays/non-modal-aria-popover';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { cx } from '@/utils/cx';

import type { AddColumnOption } from './components/add-column-header';
import { AddColumnHeader } from './components/add-column-header';
import { CheckboxCell } from './components/checkbox-cell';
import { ColumnDragOverlay } from './components/column-drag-overlay';
import { ColumnHeaderMenuTrigger } from './components/column-header-menu';
import { EditableCell } from './components/editable-cell';
import {
  EmptyStateError,
  EmptyStateNoData,
  EmptyStateNoResults,
} from './components/empty-state';
import { ResizableHeader } from './components/resizable-header';
import type { RowContextMenuAction } from './components/row-context-menu';
import { RowContextMenu } from './components/row-context-menu';
import { SkeletonRowCells, SkeletonRows } from './components/skeleton-row';
import { SortableHeader } from './components/sortable-header';
import { useColumnReorder } from './hooks/useColumnReorder';
import { useColumnResize } from './hooks/useColumnResize';
import { useKeyboardReorderAnnouncement } from './hooks/useKeyboardReorderAnnouncement';
import {
  type TableEditingState,
  useTableEditing,
} from './hooks/useTableEditing';
import { useTableKeyboard } from './hooks/useTableKeyboard';
import { styles } from './styles';
import type {
  DataTableCellState,
  DataTableColumnConfig,
  FooterRenderContext,
} from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DataTableRowData {
  /** Stable unique identifier (entity ID or similar) */
  id: string;
  /** Human-readable name used in context menu aria-label */
  name?: string;
  /** Column key → rendered cell content */
  cells: Record<string, ReactNode>;
  /**
   * Optional stable identifier for the row, rendered as `data-row-id` on the
   * row element for e2e tests. Should be the underlying entity ID. Distinct
   * from `id` (which is a virtualizer key) so callers can opt in for e2e
   * selectability without changing virtualization semantics.
   */
  rowId?: string;
}

export interface DataTableProps {
  /** Ordered list of visible column configurations */
  columns: DataTableColumnConfig[];
  /** Flat list of row data */
  rows: DataTableRowData[];
  /** Column ids that are pinned to the left pane */
  pinnedColumnIds?: string[];
  /** Size variant — controls row height and cell padding */
  size?: keyof typeof styles.sizes;
  /** Width (px) of the fixed left pane. Defaults to sum of pinned column widths. */
  leftPaneWidth?: number;
  /** Default column width in pixels when not specified in ColumnConfig */
  defaultColumnWidth?: number;
  /** Called when a row is clicked */
  onRowClick?: (rowId: string) => void;
  /** Announcement text for the aria-live region (loading, filtering, etc.) */
  statusAnnouncement?: string;
  /** Show a loading overlay */
  isLoading?: boolean;
  /** Accessible label for the table */
  'aria-label'?: string;
  /** Current selection state (Set<Key> | 'all') */
  selection?: Selection;
  /** Keys to exclude from 'all' selection (used for "all except these" semantics) */
  excludedKeys?: Set<string>;
  /** Callback when selection changes */
  onSelectionChange?: (selection: Selection) => void;
  /** Selection mode ('none' | 'single' | 'multiple') */
  selectionMode?: 'none' | 'single' | 'multiple';
  /** Current sort descriptor (controlled). When omitted, sort state is managed internally. */
  sortDescriptor?: SortDescriptor | null;
  /** Called when sort changes (controlled mode) */
  onSortChange?: (descriptor: SortDescriptor | null) => void;
  /** Per-row context menu action factories keyed by entity type */
  getRowContextActions?: (rowId: string) => Partial<{
    onEdit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onCopyLink: () => void;
    /**
     * "Open record" — when provided, the inline hover affordance shows a
     * dedicated up-right arrow button (alongside the "…" menu) that opens the
     * record's full page. Distinct from the menu actions so it reads as the
     * primary navigation, not a buried menu item.
     */
    onOpen: () => void;
    extraActions: RowContextMenuAction[];
  }>;
  /**
   * localStorage key prefix for persisting column widths and order.
   * When provided, widths and order survive page refreshes.
   * Recommended format: `table_${orgId}_${entityTypeName}`
   */
  storageKey?: string;
  /**
   * Optional factory for inline cell editing.
   * Return a config object to make a cell editable, or null/undefined for read-only.
   *
   * @param entityId - Row entity ID
   * @param colId    - Column ID (getColId result: name or kind)
   */
  getCellEditConfig?: (
    entityId: string,
    colId: string
  ) =>
    | {
        /** Property cells: the schema driving which editor renders. */
        propertyType?: PropertyTypeSchema;
        /**
         * Association cells: the association schema plus the org needed to
         * search/resolve target entities. Exactly one of propertyType or
         * association must be provided.
         */
        association?: {
          associationType: AssociationType;
          orgId: number;
          /** False for required associations (e.g. a pipeline/lifecycle stage). */
          allowClear?: boolean;
        };
        value: unknown;
        columnLabel: string;
        onSave: (value: unknown) => Promise<void>;
      }
    | null
    | undefined;
  /**
   * Which empty state variant to show when rows is empty and not loading.
   * - 'no-data':    No records exist yet (default)
   * - 'no-results': Filters/search returned no matches
   * - 'error':      Data fetch failed
   */
  emptyStateVariant?: 'no-data' | 'no-results' | 'error';
  /** Entity type label for empty state messages, e.g. "contact" or "company" */
  entityType?: string;
  /** Custom heading for the no-data empty state (overrides the default). */
  emptyTitle?: string;
  /** Custom description for the no-data empty state (overrides the default). */
  emptyDescription?: string;
  /** Error message for the error empty state variant */
  errorMessage?: string;
  /** Custom heading for the error empty state variant (overrides EmptyStateError's default). */
  errorHeading?: string;
  /** Called when "Create your first [entityType]" CTA is clicked */
  onCreateFirst?: () => void;
  /**
   * Render the no-data "Create first" CTA disabled (still visible) rather than
   * active — for when create is momentarily unavailable.
   */
  createFirstDisabled?: boolean;
  /** Called when "Clear filters" CTA is clicked */
  onClearFilters?: () => void;
  /** Called when "Retry" CTA is clicked */
  onRetry?: () => void;
  /** Called when a cell enters edit mode */
  onEditStart?: (entityId: string, propertyName: string) => void;
  /** Called when editing ends (commit or cancel) */
  onEditEnd?: () => void;
  /**
   * Total number of rows (including unloaded). When set, enables Attio-style
   * infinite scroll where the virtualizer knows the full height upfront and
   * renders placeholder rows for unloaded positions.
   */
  totalRowCount?: number;
  /**
   * Called when the virtualizer renders rows that need data.
   * Receives the start and end indices of the unloaded range.
   */
  onLoadRows?: (startIndex: number, endIndex: number) => void;
  // ---------------------------------------------------------------------------
  // Add Column Feature
  // ---------------------------------------------------------------------------
  /** Available columns for the add column dropdown */
  availableColumns?: AddColumnOption[];
  /** Callback when a column is added via the + button */
  onAddColumn?: (columnId: string) => void;
  /** Callback to create a new attribute (navigates to settings) */
  onCreateAttribute?: () => void;
  // ---------------------------------------------------------------------------
  // Column Header Menu Feature
  // ---------------------------------------------------------------------------
  /** Callback when a column is hidden via the header menu */
  onColumnHide?: (columnId: string) => void;
  /** Called with the full new right-pane column order after a drag reorder. */
  onColumnReorder?: (newOrder: string[]) => void;
  /** columnOrderControlled default/uncontrolled behavior: when true, the `columns` prop
   *  order is authoritative for ordering and localStorage is not used to seed/persist
   *  order (used while a saved view is active). When false or omitted (the default),
   *  the table runs in uncontrolled mode: order is seeded from `storageKey`-scoped
   *  localStorage on mount, and user reorders (drag or keyboard) are persisted back to
   *  that same localStorage entry. */
  columnOrderControlled?: boolean;
  /**
   * Optional render prop for a sticky footer row below the virtualized body.
   * The footer splits into left/right panes matching the header layout.
   * IMPORTANT: The returned component tree must be memoized to prevent
   * excessive re-renders during horizontal scroll sync.
   * Only rendered when rows are present or the table is loading.
   */
  renderFooter?: (context: FooterRenderContext) => ReactNode;
  // ---------------------------------------------------------------------------
  // Row Actions Display
  // ---------------------------------------------------------------------------
  /**
   * When true, row context actions (open arrow + three-dots menu) are rendered
   * inline in the first pinned column on hover, Attio-style.
   * When false (default), actions render as a separate column at the end of the row.
   */
  inlineRowActions?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_SELECTION: Selection = new Set<string>();

function getColId(col: DataTableColumnConfig): string {
  return String(col.name ?? col.kind);
}

/** Returns the human-readable label for a column header. */
function getColLabel(col: DataTableColumnConfig): string {
  return col.label ?? col.name ?? col.kind;
}

/** Returns a numeric pixel width for a column. Falls back to defaultColumnWidth for string/fr values. */
function getColWidth(
  col: DataTableColumnConfig,
  defaultColumnWidth: number
): number {
  if (typeof col.width === 'number') {
    return col.width;
  }
  if (typeof col.width === 'string') {
    const parsed = parseFloat(col.width);
    if (
      !isNaN(parsed) &&
      !col.width.includes('%') &&
      !col.width.includes('fr')
    ) {
      return parsed;
    }
  }
  return defaultColumnWidth;
}

function getHeaderHeight(size: keyof typeof styles.sizes): number {
  if (size === 'sm') {
    return 32;
  }
  if (size === 'lg') {
    return 48;
  }
  return 40;
}

// Prefetch distance in rows; kept as a constant so the load-more trigger
// no longer depends on getEstimatedRowHeight to calculate a pixel distance.
const LOAD_MORE_PREFETCH_ROWS = 50;

// Approximates the median rendered row height (single-line rows render taller
// than min-h due to cell padding + avatar/badge content). The load-more trigger
// no longer depends on this value — it uses row counts instead.
function getEstimatedRowHeight(size: keyof typeof styles.sizes): number {
  if (size === 'sm') {
    return 40;
  }
  if (size === 'lg') {
    return 64;
  }
  return 52;
}

/**
 * Compute the body container style.  During loading transitions (e.g. sort
 * change) we preserve the last known body height so the footer doesn't jump.
 */
function getBodyContainerStyle(
  isLoading: boolean,
  rowCount: number,
  totalHeight: number,
  lastHeight: number
): React.CSSProperties | undefined {
  if (isLoading) {
    if (lastHeight > 0) {
      return { flex: 1, minHeight: lastHeight, position: 'relative' };
    }
    return { flex: 1, position: 'relative' };
  }
  if (rowCount === 0) {
    return undefined;
  }
  return { height: totalHeight, position: 'relative' };
}

/** Build an aria-live sort announcement string */
function buildSortAnnouncement(descriptor: SortDescriptor | null): string {
  if (!descriptor) {
    return 'Sort cleared';
  }
  const dir = descriptor.direction === 'ascending' ? 'ascending' : 'descending';
  return `Sorted by ${String(descriptor.column)} ${dir}`;
}

// ---------------------------------------------------------------------------
// Measure element
// ---------------------------------------------------------------------------

/**
 * Returns a stable `measureElement` callback for @tanstack/react-virtual.
 * Uses synchronous getBoundingClientRect for accurate row height measurement.
 */
function useMeasureElement() {
  const measureElement = useCallback((el: Element | null) => {
    if (!el) {
      return 0;
    }
    return el.getBoundingClientRect().height;
  }, []);

  return measureElement;
}

// ---------------------------------------------------------------------------
// DropZoneStart/End — virtual drop targets at the edges of the header
// ---------------------------------------------------------------------------

const DROP_ZONE_START_ID = '__drop-zone-start';
const DROP_ZONE_END_ID = '__drop-zone-end';

function DropZoneStart() {
  const { setNodeRef } = useDroppable({ id: DROP_ZONE_START_ID });
  // Zero-width drop target - doesn't affect layout but still receives drop events
  // Uses padding to create a hit area for the droppable sensor
  return (
    <div
      ref={setNodeRef}
      style={{
        width: 0,
        minWidth: 0,
        height: '100%',
        padding: '0 8px',
        margin: '0 -8px',
      }}
    />
  );
}

function DropZoneEnd() {
  const { setNodeRef } = useDroppable({ id: DROP_ZONE_END_ID });
  // Zero-width drop target - doesn't affect layout but still receives drop events
  return (
    <div
      ref={setNodeRef}
      style={{
        width: 0,
        minWidth: 0,
        height: '100%',
        padding: '0 8px',
        margin: '0 -8px',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// InlineCellWithActions — Wraps cell content with Attio-style hover actions
// ---------------------------------------------------------------------------

interface InlineAction {
  key: string;
  label: string;
  icon?: React.FC<{ className?: string }>;
  isDestructive?: boolean;
  isDisabled?: boolean;
  onAction: () => void | Promise<void>;
}

interface InlineCellWithActionsProps {
  children: ReactNode;
  actions: InlineAction[];
  /** When set, a hover-only up-right arrow opens the record's full page. */
  onOpen?: () => void;
  isHovered: boolean;
  entityName?: string;
}

function InlineCellWithActions({
  children,
  actions,
  onOpen,
  isHovered,
  entityName,
}: InlineCellWithActionsProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleMenuAction = useCallback(
    async (key: string) => {
      const action = actions.find(a => a.key === key);
      if (action) {
        try {
          await action.onAction();
        } catch (err) {
          toast.error('Action failed. Please try again.');
          console.error('Context menu action failed:', err);
        }
      }
      setIsMenuOpen(false);
      triggerRef.current?.focus();
    },
    [actions]
  );

  const showActions = isHovered || isMenuOpen;
  const hasActions = actions.length > 0;

  return (
    <div className='flex items-center gap-2 min-w-0 w-full'>
      {/* Cell content - full width when no actions shown, shrinks when actions visible */}
      <div className='min-w-0 flex-1'>{children}</div>

      {/* Action buttons - only rendered on hover to avoid taking space */}
      {(hasActions || onOpen) && showActions && (
        <div
          className='flex items-center gap-0.5 shrink-0'
          onClick={e => e.stopPropagation()}
        >
          {/* Open full record — up-right arrow, hover-only primary nav. */}
          {onOpen && (
            <AriaButton
              aria-label={entityName ? `Open ${entityName}` : 'Open record'}
              onPress={onOpen}
              className={cx(
                'flex items-center justify-center rounded p-1',
                'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                'hover:bg-[var(--color-bg-tertiary)]',
                'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-1',
                'transition-colors duration-75 cursor-pointer'
              )}
            >
              <ArrowUpRight className='size-4' />
            </AriaButton>
          )}

          {/* Three-dots menu - compact size matching column header menus */}
          {actions.length > 0 && (
            <AriaMenuTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <AriaButton
                ref={triggerRef}
                aria-label={
                  entityName ? `Actions for ${entityName}` : 'Row actions'
                }
                className={cx(
                  'flex items-center justify-center rounded p-1',
                  'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                  'hover:bg-[var(--color-bg-tertiary)]',
                  'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-1',
                  'transition-colors duration-75 cursor-pointer'
                )}
              >
                <DotsHorizontal className='size-4' />
              </AriaButton>
              <NonModalAriaPopover
                placement='bottom end'
                data-component='menu'
                className={cx(
                  'w-32 overflow-hidden rounded-md bg-primary shadow-lg ring-1 ring-secondary_alt',
                  'origin-(--trigger-anchor-point) will-change-transform',
                  'entering:duration-150 entering:ease-out entering:animate-in entering:fade-in entering:slide-in-from-top-0.5',
                  'exiting:duration-100 exiting:ease-in exiting:animate-out exiting:fade-out exiting:slide-out-to-top-0.5'
                )}
              >
                {state => (
                  <>
                    {/* Ready marker for e2e helpers: present once the popover
                        has finished its entrance animation and not yet started
                        exiting, mirroring the pattern in Dropdown.Popover so
                        the shared `menu(page).waitForReady()` helper resolves
                        for both menu surfaces. */}
                    <div
                      data-component='menu-ready-marker'
                      aria-hidden='true'
                      style={{ display: 'none' }}
                      data-ready={
                        !state.isEntering && !state.isExiting ? '' : undefined
                      }
                    />
                    <AriaMenu
                      aria-label={
                        entityName ? `Actions for ${entityName}` : 'Row actions'
                      }
                      onAction={key => void handleMenuAction(String(key))}
                      disabledKeys={actions
                        .filter(a => a.isDisabled)
                        .map(a => a.key)}
                      className='py-0.5 outline-none'
                    >
                      {actions.map((action, index) => {
                        const isFirst = index === 0;
                        const isFirstDestructive =
                          action.isDestructive &&
                          !actions.slice(0, index).some(a => a.isDestructive);
                        const showSeparator = !isFirst && isFirstDestructive;
                        const Icon = action.icon;

                        return (
                          <Fragment key={action.key}>
                            {showSeparator && (
                              <AriaSeparator className='my-0.5 h-px bg-border-secondary' />
                            )}
                            <AriaMenuItem
                              id={action.key}
                              textValue={action.label}
                              className={cx(
                                'px-1 outline-none cursor-pointer',
                                'focus:bg-primary_hover'
                              )}
                            >
                              <div
                                className={cx(
                                  'flex items-center gap-1.5 px-2 py-1.5 rounded',
                                  action.isDestructive &&
                                    'text-[var(--color-text-error-primary)]'
                                )}
                              >
                                {Icon && (
                                  <Icon className='size-3.5 text-fg-quaternary shrink-0' />
                                )}
                                <span
                                  className={cx(
                                    'flex-1 text-xs font-medium',
                                    action.isDestructive
                                      ? 'text-[var(--color-text-error-primary)]'
                                      : 'text-[var(--color-text-secondary)]'
                                  )}
                                >
                                  {action.label}
                                </span>
                              </div>
                            </AriaMenuItem>
                          </Fragment>
                        );
                      })}
                    </AriaMenu>
                  </>
                )}
              </NonModalAriaPopover>
            </AriaMenuTrigger>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTableEditableCell — stable-reference wrapper for editable table cells
// ---------------------------------------------------------------------------

interface DataTableEditableCellProps {
  rowId: string;
  colId: string;
  editConfig: NonNullable<
    ReturnType<NonNullable<DataTableProps['getCellEditConfig']>>
  >;
  editing: TableEditingState;
  isCellEditing: boolean;
  cols: DataTableColumnConfig[];
  colIdx: number;
  rows: DataTableRowData[];
  rowIndex: number;
  getCellEditConfig: DataTableProps['getCellEditConfig'];
}

function DataTableEditableCell({
  rowId,
  colId,
  editConfig,
  editing,
  isCellEditing,
  cols,
  colIdx,
  rows,
  rowIndex,
  getCellEditConfig,
}: DataTableEditableCellProps) {
  const { startEdit, commitEdit, cancelEdit } = editing;

  const onStartEdit = useCallback(
    () => startEdit(rowId, colId),
    [startEdit, rowId, colId]
  );

  // Commit/cancel carry THIS cell's identity so a save that resolves after
  // the user already started editing another cell can't close the newer
  // editor (blur commits are RAF-delayed and the save awaits the network).
  const onCommitEdit = useCallback(
    () => commitEdit({ entityId: rowId, propertyName: colId }),
    [commitEdit, rowId, colId]
  );

  const onCancelEdit = useCallback(
    () => cancelEdit({ entityId: rowId, propertyName: colId }),
    [cancelEdit, rowId, colId]
  );

  const handleTabNext = useCallback(() => {
    const restCols = cols.slice(colIdx + 1);
    for (const nextCol of restCols) {
      const nextColId = getColId(nextCol);
      const nextConfig = getCellEditConfig
        ? getCellEditConfig(rowId, nextColId)
        : null;
      if (nextConfig) {
        startEdit(rowId, nextColId);
        return;
      }
    }
    commitEdit({ entityId: rowId, propertyName: colId });
  }, [startEdit, commitEdit, cols, colIdx, rowId, colId, getCellEditConfig]);

  const handleEnterNext = useCallback(() => {
    const nextRow = rows[rowIndex + 1];
    if (nextRow && getCellEditConfig) {
      const nextConfig = getCellEditConfig(nextRow.id, colId);
      if (nextConfig) {
        startEdit(nextRow.id, colId);
        return;
      }
    }
    commitEdit({ entityId: rowId, propertyName: colId });
  }, [startEdit, commitEdit, rows, rowIndex, rowId, colId, getCellEditConfig]);

  return (
    <EditableCell
      entityId={rowId}
      propertyName={colId}
      propertyType={editConfig.propertyType}
      association={editConfig.association}
      value={editConfig.value}
      columnLabel={editConfig.columnLabel}
      isEditing={isCellEditing}
      onStartEdit={onStartEdit}
      onCommitEdit={onCommitEdit}
      onCancelEdit={onCancelEdit}
      onSave={editConfig.onSave}
      onTabNext={handleTabNext}
      onEnterNext={handleEnterNext}
    />
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export const DataTable = ({
  columns,
  rows,
  pinnedColumnIds = [],
  size = 'md',
  leftPaneWidth,
  defaultColumnWidth = 160,
  onRowClick,
  statusAnnouncement,
  isLoading = false,
  'aria-label': ariaLabel = 'Data table',
  selection = EMPTY_SELECTION,
  excludedKeys,
  onSelectionChange,
  selectionMode = 'none',
  sortDescriptor: controlledSort,
  onSortChange,
  getRowContextActions,
  getCellEditConfig,
  storageKey,
  emptyStateVariant = 'no-data',
  entityType,
  emptyTitle,
  emptyDescription,
  errorMessage,
  errorHeading,
  onCreateFirst,
  createFirstDisabled,
  onClearFilters,
  onRetry,
  onEditStart,
  onEditEnd,
  totalRowCount,
  onLoadRows,
  availableColumns,
  onAddColumn,
  onCreateAttribute,
  onColumnHide,
  onColumnReorder,
  columnOrderControlled,
  renderFooter,
  inlineRowActions = false,
}: DataTableProps) => {
  // Track hovered row index for synchronized highlighting across panes
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const handleRowMouseEnter = useCallback(
    (index: number) => () => setHoveredRowIndex(index),
    [setHoveredRowIndex]
  );
  const handleRowMouseLeave = useCallback(
    () => setHoveredRowIndex(null),
    [setHoveredRowIndex]
  );

  // Stable anchor for Shift+click range selection — tracks the last
  // explicitly selected row so consecutive Shift+clicks extend from
  // a fixed point rather than drifting with Set insertion order.
  const selectionAnchorRef = useRef<string | null>(null);

  // Inline cell editing state — only one cell can be in edit mode at a time.
  // Destructure immediately so each piece is a stable dependency for useMemo
  // (the hook returns a new object literal each render, but each callback is
  // individually stable via useCallback).
  const {
    startEdit: rawStartEdit,
    commitEdit: rawCommitEdit,
    cancelEdit: rawCancelEdit,
    isEditing,
    editingCell,
  } = useTableEditing();

  // Wrap editing lifecycle to notify parent component. commit/cancel accept
  // the identity of the cell whose edit ended so an async save landing after
  // the user already opened a different cell can't close the newer editor
  // (see useTableEditing).
  const editing = useMemo(
    () => ({
      editingCell,
      isEditing,
      startEdit: (entityId: string, propertyName: string) => {
        rawStartEdit(entityId, propertyName);
        onEditStart?.(entityId, propertyName);
      },
      commitEdit: (cell?: DataTableCellState) => {
        rawCommitEdit(cell);
        onEditEnd?.();
      },
      cancelEdit: (cell?: DataTableCellState) => {
        rawCancelEdit(cell);
        onEditEnd?.();
      },
    }),
    [
      rawStartEdit,
      rawCommitEdit,
      rawCancelEdit,
      isEditing,
      editingCell,
      onEditStart,
      onEditEnd,
    ]
  );

  const selectionEnabled = selectionMode !== 'none';
  const checkboxWidth = 48; // Width for checkbox column

  const pinnedSet = useMemo(() => new Set(pinnedColumnIds), [pinnedColumnIds]);
  const leftColumns = useMemo(
    () => columns.filter(c => pinnedSet.has(getColId(c))),
    [columns, pinnedSet]
  );
  const rightColumns = useMemo(
    () => columns.filter(c => !pinnedSet.has(getColId(c))),
    [columns, pinnedSet]
  );

  // ---------------------------------------------------------------------------
  // Column resize & reorder
  // ---------------------------------------------------------------------------

  // Detect touch devices — disable drag-to-reorder (conflicts with scroll);
  // resize handles remain active via pointer events with a wide touch target.
  const isTouchDevice =
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  const resize = useColumnResize(storageKey);

  // Pass column IDs to the reorder hook.
  // The hook uses string comparison internally, so a new array each render is safe.
  const reorderOptions = useMemo(
    () => ({ controlled: columnOrderControlled, onReorder: onColumnReorder }),
    [columnOrderControlled, onColumnReorder]
  );
  const reorder = useColumnReorder(
    rightColumns.map(c => getColId(c)),
    storageKey,
    reorderOptions
  );

  // Destructure stable values from reorder to avoid re-creating callbacks
  // every render (useColumnReorder returns a new object literal each render).
  const { columnOrder, reorderByKeyboard, activeId, overId, dragDeltaX } =
    reorder;

  // Apply stored/dragged order to the right-pane columns
  const orderedRightColumns = useMemo(() => {
    const orderMap = new Map(
      columnOrder.map((id, idx) => [id, idx] as [string, number])
    );
    return [...rightColumns].sort((a, b) => {
      const orderA = orderMap.get(getColId(a)) ?? Infinity;
      const orderB = orderMap.get(getColId(b)) ?? Infinity;
      return orderA - orderB;
    });
  }, [rightColumns, columnOrder]);

  // Compute which column should show the drop indicator and on which edge
  const dropIndicatorTarget = useMemo(() => {
    if (!activeId || !overId) {
      return null;
    }
    // Special case: hovering over the virtual drop zone at the start
    // Show the indicator on the leftmost column's 'before' edge
    if (overId === DROP_ZONE_START_ID) {
      const firstColId = columnOrder[0];
      if (firstColId) {
        return {
          colId: firstColId,
          position: 'before' as const,
        };
      }
      return null;
    }
    // Special case: hovering over the virtual drop zone at the end
    // Show the indicator on the rightmost column's 'after' edge
    if (overId === DROP_ZONE_END_ID) {
      const lastColId = columnOrder[columnOrder.length - 1];
      if (lastColId) {
        return {
          colId: lastColId,
          position: 'after' as const,
        };
      }
      return null;
    }
    const overIdx = columnOrder.indexOf(overId);
    if (overIdx === -1) {
      return null;
    }
    // Use dragDeltaX to determine which edge to show indicator on:
    // - Positive dragDeltaX = dragged element center is RIGHT of hovered element center → 'after'
    // - Negative dragDeltaX = dragged element center is LEFT of hovered element center → 'before'
    // This makes the indicator switch at the middle of each column
    return {
      colId: overId,
      position: dragDeltaX >= 0 ? ('after' as const) : ('before' as const),
    };
  }, [activeId, overId, columnOrder, dragDeltaX]);

  // Calculate the absolute X position for the single drop indicator line
  // This renders ONE indicator at the table level instead of per-column indicators
  const dropIndicatorX = useMemo(() => {
    if (!dropIndicatorTarget) {
      return null;
    }
    const { colId, position } = dropIndicatorTarget;

    // Sum widths of all columns before the target position
    let x = 0;
    for (const col of orderedRightColumns) {
      const id = getColId(col);
      const colWidth = resize.getColumnWidth(
        id,
        getColWidth(col, defaultColumnWidth)
      );
      if (id === colId) {
        // For 'after', add this column's width to position at right edge
        if (position === 'after') {
          x += colWidth;
        }
        break;
      }
      x += colWidth;
    }
    return x;
  }, [dropIndicatorTarget, orderedRightColumns, resize, defaultColumnWidth]);

  // Track last known drop indicator position so it fades out in place
  const lastDropIndicatorXRef = useRef<number>(0);
  if (dropIndicatorX !== null) {
    lastDropIndicatorXRef.current = dropIndicatorX;
  }

  // Get active column info for drag overlay
  const activeColumn = useMemo(() => {
    if (!activeId) {
      return null;
    }
    return orderedRightColumns.find(c => getColId(c) === activeId) ?? null;
  }, [activeId, orderedRightColumns]);

  // Announcement for keyboard-driven reorder (aria-live)
  const { reorderAnnouncement, notifyKeyboardMove } =
    useKeyboardReorderAnnouncement(columnOrder, rightColumns);

  const handleKeyboardReorder = useCallback(
    (colId: string, direction: 'left' | 'right') => {
      notifyKeyboardMove(colId, direction);
      reorderByKeyboard(colId, direction);
    },
    [notifyKeyboardMove, reorderByKeyboard]
  );

  const computedLeftPaneWidth =
    leftPaneWidth ??
    leftColumns.reduce(
      (sum, c) =>
        sum +
        resize.getColumnWidth(getColId(c), getColWidth(c, defaultColumnWidth)),
      0
    ) + (selectionEnabled ? checkboxWidth : 0);

  // Total width of right (scrollable) columns - used to set minWidth on rows
  const computedRightPaneContentWidth = orderedRightColumns.reduce(
    (sum, c) =>
      sum +
      resize.getColumnWidth(getColId(c), getColWidth(c, defaultColumnWidth)),
    0
  );

  // Add extra width for the add column button (48px) plus scroll padding (100px)
  // so user can always scroll to see the add column button
  const addColumnExtraWidth =
    onAddColumn && availableColumns && availableColumns.length > 0 ? 148 : 0;

  // Pre-computed pixel widths for all columns, keyed by column ID.
  // Passed to renderFooter so the footer uses the exact same widths as header/body.
  const columnWidths = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of [...leftColumns, ...orderedRightColumns]) {
      const colId = getColId(c);
      map[colId] = resize.getColumnWidth(
        colId,
        getColWidth(c, defaultColumnWidth)
      );
    }
    return map;
  }, [leftColumns, orderedRightColumns, resize, defaultColumnWidth]);

  // Single scroll container for both vertical and horizontal scrolling
  // Header and body scroll together — eliminates header lag during horizontal scroll
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  // Left pane body ref for forwarding wheel events
  const leftBodyRef = useRef<HTMLDivElement>(null);
  // Footer right-pane ref for horizontal scroll sync
  // rightFooterRef removed — footer is now inside bodyScrollRef and
  // scrolls horizontally via CSS (no JS sync needed)

  // Sort state — uncontrolled unless sortDescriptor prop provided
  const [internalSort, setInternalSort] = useState<SortDescriptor | null>(null);
  const [sortAnnouncement, setSortAnnouncement] = useState('');

  const isControlled = controlledSort !== undefined;
  const activeSortDescriptor = isControlled ? controlledSort : internalSort;

  const handleSortChange = useCallback(
    (descriptor: SortDescriptor | null) => {
      const announcement = buildSortAnnouncement(descriptor);
      setSortAnnouncement(announcement);
      if (isControlled) {
        onSortChange?.(descriptor);
      } else {
        setInternalSort(descriptor);
      }
    },
    [isControlled, onSortChange]
  );

  // Track whether scroll container overflows vertically — when it does,
  // the footer is stuck at the bottom (sticky) and doesn't need a bottom
  // border.  When content fits, the footer floats mid-screen and needs a
  // bottom border to visually close the table.
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);

  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) {
      return;
    }
    const check = () =>
      setHasVerticalOverflow(el.scrollHeight > el.clientHeight);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    check();
    return () => ro.disconnect();
  }, []);

  // Footer horizontal scroll is handled by CSS — it's inside bodyScrollRef
  // with sticky bottom-0, so it scrolls with the header/body natively.

  // Forward horizontal wheel events from left pane to main scroll container (Attio-style)
  // Also prevents browser back/forward gesture on horizontal swipe
  const handleLeftPaneWheel = useCallback((e: React.WheelEvent) => {
    if (!bodyScrollRef.current) {
      return;
    }
    // Handle horizontal scroll (shift+wheel or horizontal trackpad gesture)
    if (e.deltaX !== 0) {
      bodyScrollRef.current.scrollLeft += e.deltaX;
    }
  }, []);

  const measureElement = useMeasureElement();

  // Attio-style infinite scroll: virtualizer knows total count, not just loaded
  // rows. Clamp to rows.length so a stale/lower total (e.g. a server count not
  // yet adjusted for an optimistically injected row) can never make rows in
  // hand unpaintable. Math.max preserves the infinite-scroll case where
  // totalRowCount legitimately exceeds the loaded rows.
  const effectiveRowCount = Math.max(totalRowCount ?? 0, rows.length);

  const virtualizer = useVirtualizer({
    count: effectiveRowCount,
    getScrollElement: () => bodyScrollRef.current,
    estimateSize: () => getEstimatedRowHeight(size),
    measureElement,
    // Higher overscan for smoother scrolling - pre-render more rows above/below viewport
    overscan: 15,
    getItemKey: index => rows[index]?.id ?? `placeholder-${index}`,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();
  const headerHeight = getHeaderHeight(size);

  // Remember the last body height so the container doesn't collapse during
  // loading transitions (e.g. sort change).  This keeps the footer pinned
  // at the viewport bottom instead of jumping up to follow the skeleton.
  const lastBodyHeightRef = useRef<number>(0);
  if (!isLoading && rows.length > 0) {
    lastBodyHeightRef.current = totalHeight;
  }

  // Track which page ranges have been requested to avoid duplicate fetches
  const loadedRangeRef = useRef<number>(rows.length);

  // Gate: only auto-load after the user has actually scrolled.
  // Prevents the on-mount cascade where the virtualizer over-reports visible
  // rows (estimate < real row height) and fires ~8 pages before stabilising.
  const hasUserScrolledRef = useRef(false);

  // Load the next page of rows if the user is close to the unloaded boundary.
  const maybeLoadMore = useCallback(() => {
    if (!onLoadRows || !totalRowCount || totalRowCount <= rows.length) {
      return;
    }
    if (!hasUserScrolledRef.current) {
      return;
    }

    const items = virtualizer.getVirtualItems();
    const lastVisible = items.length ? Math.max(...items.map(i => i.index)) : 0;
    const distanceToUnloaded = rows.length - lastVisible - 1;

    if (
      distanceToUnloaded <= LOAD_MORE_PREFETCH_ROWS &&
      rows.length < totalRowCount
    ) {
      const start = rows.length;
      const end = Math.min(
        rows.length + LOAD_MORE_PREFETCH_ROWS - 1,
        totalRowCount - 1
      );
      if (start >= loadedRangeRef.current) {
        loadedRangeRef.current = end + 1;
        onLoadRows(start, end);
      }
    }
  }, [onLoadRows, totalRowCount, rows.length, virtualizer]);

  // Continuation effect: keep paging while the user stays near the boundary
  // after a fling. On mount this is a no-op because hasUserScrolledRef is false.
  useEffect(() => {
    maybeLoadMore();
  }, [rows.length, maybeLoadMore]);

  // Reset loaded range and scroll gate when rows shrink (e.g. filter/search change),
  // preserving both refs when rows grow from new data loading.
  useEffect(() => {
    if (rows.length < loadedRangeRef.current) {
      loadedRangeRef.current = rows.length;
      hasUserScrolledRef.current = false;
    }
  }, [rows.length]);

  const handleBodyScroll = useCallback(() => {
    hasUserScrolledRef.current = true;
    maybeLoadMore();
  }, [maybeLoadMore]);

  // ---------------------------------------------------------------------------
  // Selection handling
  // ---------------------------------------------------------------------------

  const selectedRowIds = useMemo(() => {
    if (selection === 'all') {
      // When selection is 'all', include all rows except those in excludedKeys
      const allIds = new Set(rows.map(r => r.id));
      if (excludedKeys) {
        for (const id of excludedKeys) {
          allIds.delete(id);
        }
      }
      return allIds;
    }
    return selection;
  }, [selection, rows, excludedKeys]);
  // Verify actual membership to handle stale selection IDs after filtering
  // When selection is 'all' with exclusions, check if all visible rows are actually selected
  const isAllSelected = useMemo(() => {
    if (selection === 'all') {
      // All selected only if no visible rows are excluded
      return excludedKeys ? rows.every(r => !excludedKeys.has(r.id)) : true;
    }
    return rows.length > 0 && rows.every(r => selectedRowIds.has(r.id));
  }, [selection, excludedKeys, rows, selectedRowIds]);
  // Count how many current rows are selected for indeterminate state
  // Use selectedRowIds which already accounts for excludedKeys
  const selectedCount = rows.filter(r => selectedRowIds.has(r.id)).length;
  const isSomeSelected = selectedCount > 0 && selectedCount < rows.length;

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) {
      return;
    }

    // If all selected, deselect all
    if (isAllSelected) {
      onSelectionChange(new Set<string>());
      return;
    }

    // Select all
    onSelectionChange('all');
  }, [onSelectionChange, isAllSelected]);

  const handleRowSelection = useCallback(
    (rowId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
      if (!onSelectionChange || !selectionEnabled) {
        return;
      }

      // When selection is 'all', filter out excludedKeys to maintain "all except these" semantics
      const currentSelection =
        selection === 'all'
          ? new Set(rows.map(r => r.id).filter(id => !excludedKeys?.has(id)))
          : new Set(selection);

      // Single selection mode
      if (selectionMode === 'single') {
        if (currentSelection.has(rowId)) {
          onSelectionChange(new Set<string>());
        } else {
          onSelectionChange(new Set([rowId]));
        }
        return;
      }

      // Multiple selection mode
      // Shift+click for range selection
      if (
        event &&
        'shiftKey' in event &&
        event.shiftKey &&
        selectionAnchorRef.current
      ) {
        const anchorIndex = rows.findIndex(
          r => r.id === selectionAnchorRef.current
        );
        const currentIndex = rows.findIndex(r => r.id === rowId);

        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex);
          const end = Math.max(anchorIndex, currentIndex);
          const rangeIds = rows.slice(start, end + 1).map(r => r.id);
          const newSelection = new Set([...currentSelection, ...rangeIds]);
          onSelectionChange(newSelection);
          // Anchor stays fixed during Shift+click ranges
          return;
        }
      }

      // Toggle selection and update anchor
      if (currentSelection.has(rowId)) {
        currentSelection.delete(rowId);
        // Clear anchor if deselected
        if (selectionAnchorRef.current === rowId) {
          selectionAnchorRef.current = null;
        }
      } else {
        currentSelection.add(rowId);
        selectionAnchorRef.current = rowId;
      }
      onSelectionChange(currentSelection);
    },
    [
      onSelectionChange,
      selectionEnabled,
      selectionMode,
      selection,
      rows,
      excludedKeys,
    ]
  );

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  const totalColumns = (selectionEnabled ? 1 : 0) + columns.length;

  const handleRowSelectCallback = useCallback(
    (rowId: string, event: React.KeyboardEvent) =>
      handleRowSelection(rowId, event),
    [handleRowSelection]
  );

  const tableRef = useRef<HTMLDivElement>(null);

  const keyboard = useTableKeyboard({
    rowCount: rows.length,
    columnCount: totalColumns,
    onRowActivate: onRowClick,
    onRowSelect: handleRowSelectCallback,
    getRowId: rowIndex => rows[rowIndex]?.id,
    selection,
    selectionEnabled,
    containerRef: tableRef,
  });

  // Combined live announcement: prefer sort/reorder, then explicit status, then loading
  const loadingAnnouncement = isLoading
    ? `Loading ${entityType ? `${entityType}s` : 'data'}…`
    : '';
  const liveAnnouncement =
    sortAnnouncement ||
    reorderAnnouncement ||
    statusAnnouncement ||
    loadingAnnouncement;

  // Reset sort announcement after a brief delay so re-announcing same sort works
  useEffect(() => {
    if (!sortAnnouncement) {
      return;
    }
    const timer = setTimeout(() => setSortAnnouncement(''), 2000);
    return () => clearTimeout(timer);
  }, [sortAnnouncement]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderCheckboxHeader = () => {
    if (!selectionEnabled) {
      return null;
    }

    // Checkbox header wrapper uses role='columnheader' to ensure it is
    // announced to screen readers as part of the table structure.
    // No border-r: checkbox blends with adjacent pinned column
    // justify-center: centers checkbox in column
    return (
      <div
        key='checkbox-header'
        role='columnheader'
        style={{ width: checkboxWidth, minWidth: checkboxWidth }}
        className={cx(
          styles.common.headerCell,
          styles.sizes[size].headerCell,
          'flex items-center justify-center border-r-0'
        )}
      >
        <CheckboxCell
          isChecked={isAllSelected}
          isIndeterminate={isSomeSelected}
          onChange={() => handleSelectAll()}
          aria-label='Select all rows'
        />
      </div>
    );
  };

  const renderCheckboxCell = (row: DataTableRowData, rowIndex: number) => {
    if (!selectionEnabled) {
      return null;
    }

    const isSelected = selectedRowIds.has(row.id);

    // Checkbox cell wrapper omits role='gridcell' — it lives in the left
    // pane where the parent row is role='presentation'. The interactive
    // CheckboxCell (role='checkbox') remains accessible regardless.
    // No border-r: checkbox blends with adjacent pinned column
    // justify-center: centers checkbox in column
    return (
      <div
        key='checkbox-cell'
        ref={el => keyboard.registerCellRef(rowIndex, 0, el)}
        style={{ width: checkboxWidth, minWidth: checkboxWidth }}
        className={cx(
          styles.common.cell,
          styles.sizes[size].cell,
          'flex items-center justify-center border-r-0'
        )}
        tabIndex={keyboard.getCellTabIndex(rowIndex, 0)}
        onKeyDown={e => {
          // Space/Enter on the checkbox column toggles selection
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            handleRowSelection(row.id, e);
            return;
          }
          keyboard.handleKeyDown(e, rowIndex, 0);
        }}
      >
        <CheckboxCell
          isChecked={isSelected}
          onChange={(_, event) => handleRowSelection(row.id, event)}
          aria-label={`Select row ${row.id}`}
        />
      </div>
    );
  };

  /**
   * Render header cells for the left (pinned) pane.
   * Supports resize handles but not drag-to-reorder (pinned columns are fixed).
   * Shows column menu with sorting options (move/hide disabled for pinned columns).
   */
  const renderPinnedHeaderCells = (
    cols: DataTableColumnConfig[],
    includeCheckbox: boolean = false
  ) => {
    const cells = cols.map((col, idx) => {
      const colId = getColId(col);
      const colLabel = getColLabel(col);
      const colWidth = resize.getColumnWidth(
        colId,
        getColWidth(col, defaultColumnWidth)
      );
      const isSortableProp = col.sortable !== false;
      const isFirstPinnedCol = idx === 0;
      // The first pinned column drops its left padding only to sit tight
      // against the selection checkbox. With no selection column (read-only
      // seats, single-action lists) the checkbox gutter is gone, so the
      // column keeps the normal cell padding instead of hugging the edge.
      const hugsSelectionColumn = isFirstPinnedCol && selectionEnabled;

      let colAriaSort: 'none' | 'ascending' | 'descending' = 'none';
      let currentSortDirection: 'asc' | 'desc' | null = null;
      // Check if this column is sorted. For object_name columns, also check ahoy_display_name
      // since the CRM page maps object_name -> ahoy_display_name for backend sorting.
      const sortColumn = activeSortDescriptor?.column;
      const isThisColumnSorted =
        sortColumn === colId ||
        (colId === 'object_name' && sortColumn === 'ahoy_display_name');
      if (isThisColumnSorted) {
        colAriaSort =
          activeSortDescriptor?.direction === 'ascending'
            ? 'ascending'
            : 'descending';
        currentSortDirection =
          activeSortDescriptor?.direction === 'ascending' ? 'asc' : 'desc';
      }

      // Show column menu for sortable pinned columns
      const showColumnMenu =
        isSortableProp && (onColumnReorder || onColumnHide);

      // Left-pane and right-pane header rows both use role='row' with aria-rowindex=1
      // to ensure all column headers are accessible to screen readers.
      return (
        <div
          key={colId}
          role='columnheader'
          aria-sort={colAriaSort}
          style={{ width: colWidth, minWidth: colWidth, position: 'relative' }}
          className={cx(
            styles.common.headerCell,
            styles.sizes[size].headerCell,
            'p-0 group',
            hugsSelectionColumn && 'pl-0'
          )}
        >
          {/* Header content with optional column menu trigger */}
          <div className='flex items-center flex-1 min-w-0'>
            {(() => {
              // When column menu is shown, just render the label (menu has sort control)
              if (showColumnMenu) {
                return (
                  <span
                    className={cx(
                      'truncate text-xs font-medium text-[var(--color-text-tertiary)]',
                      hugsSelectionColumn ? 'pl-0' : 'px-3'
                    )}
                  >
                    {colLabel}
                  </span>
                );
              }
              if (isSortableProp) {
                return (
                  <SortableHeader
                    columnKey={colId}
                    label={colLabel}
                    sortDescriptor={activeSortDescriptor}
                    onSortChange={handleSortChange}
                    className={hugsSelectionColumn ? 'pl-0' : undefined}
                  />
                );
              }
              return (
                <span
                  className={cx(
                    'truncate',
                    hugsSelectionColumn ? 'pl-0' : 'px-3'
                  )}
                >
                  {colLabel}
                </span>
              );
            })()}

            {/* Column menu trigger for pinned columns (sort only, move/hide disabled) */}
            {showColumnMenu && (
              <ColumnHeaderMenuTrigger
                columnLabel={colLabel}
                sortDirection={currentSortDirection}
                isFirst={true}
                isLast={true}
                isPinned={true}
                canSort={isSortableProp}
                onSort={(direction: 'asc' | 'desc') => {
                  handleSortChange({
                    column: colId,
                    direction: direction === 'asc' ? 'ascending' : 'descending',
                  });
                }}
                onMoveLeft={() => {}}
                onMoveRight={() => {}}
                onHide={() => {}}
              />
            )}
          </div>

          {/* Resize handle for pinned columns — wide touch target with narrow visible line */}
          <div
            role='separator'
            aria-orientation='vertical'
            aria-label={`Resize ${colLabel} column`}
            tabIndex={0}
            className='absolute top-0 bottom-0 z-10 flex items-stretch justify-center cursor-col-resize group/resize'
            style={{
              width: 16,
              right: 0,
              transform: 'translateX(50%)',
              touchAction: 'none',
            }}
            onPointerDown={e => {
              e.preventDefault();
              e.stopPropagation();
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              resize.startResize(colId, e.clientX, colWidth, e.pointerId);
            }}
            onKeyDown={e => {
              const step = 10;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const delta = e.key === 'ArrowLeft' ? -step : step;
                const current = resize.getColumnWidth(colId, colWidth);
                resize.setColumnWidth(colId, current + delta);
              }
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className={cx(
                'w-[3px] transition-colors duration-75',
                'group-hover/resize:bg-[var(--color-brand-500)]',
                resize.resizingColId === colId && 'bg-[var(--color-brand-600)]'
              )}
            />
          </div>
        </div>
      );
    });

    if (includeCheckbox) {
      return [renderCheckboxHeader(), ...cells];
    }
    return cells;
  };

  /**
   * Render header cells for the right (scrollable) pane.
   * Uses ResizableHeader which supports both drag-to-reorder and resize.
   * Optionally shows ColumnHeaderMenu when onColumnReorder/onColumnHide provided.
   */
  const renderResizableHeaderCells = (cols: DataTableColumnConfig[]) =>
    cols.map((col, idx) => {
      const colId = getColId(col);
      const colLabel = getColLabel(col);
      const colWidth = resize.getColumnWidth(
        colId,
        getColWidth(col, defaultColumnWidth)
      );
      // Determine current sort state for this column
      let currentSortDirection: 'asc' | 'desc' | null = null;
      if (activeSortDescriptor?.column === colId) {
        currentSortDirection =
          activeSortDescriptor.direction === 'ascending' ? 'asc' : 'desc';
      }

      // Build column menu props if column actions are enabled.
      // reorder.reorderByKeyboard updates internal order and fires onReorder → onColumnReorder.
      const columnMenuProps =
        onColumnReorder || onColumnHide
          ? {
              sortDirection: currentSortDirection as 'asc' | 'desc' | null,
              // isFirst correctly identifies first non-pinned column; Move left is disabled for this column
              isFirst: idx === 0,
              isLast: idx === cols.length - 1,
              canSort: col.sortable !== false,
              onSort: (direction: 'asc' | 'desc') => {
                handleSortChange({
                  column: colId,
                  direction: direction === 'asc' ? 'ascending' : 'descending',
                });
              },
              onMoveLeft: () => {
                reorder.reorderByKeyboard(colId, 'left');
              },
              onMoveRight: () => {
                reorder.reorderByKeyboard(colId, 'right');
              },
              onHide: () => onColumnHide?.(colId),
            }
          : undefined;

      return (
        <ResizableHeader
          key={colId}
          colId={colId}
          label={colLabel}
          width={colWidth}
          size={size}
          isSortable={col.sortable !== false}
          sortDescriptor={activeSortDescriptor}
          onSortChange={handleSortChange}
          onResizeStart={resize.startResize}
          isResizingThis={resize.resizingColId === colId}
          onKeyboardReorder={handleKeyboardReorder}
          onKeyboardResize={(id, delta) => {
            const current = resize.getColumnWidth(
              id,
              getColWidth(col, defaultColumnWidth)
            );
            resize.setColumnWidth(id, current + delta);
          }}
          disabled={isTouchDevice}
          columnMenuProps={columnMenuProps}
        />
      );
    });

  const renderRowCells = (
    row: DataTableRowData,
    cols: DataTableColumnConfig[],
    rowIndex: number,
    includeCheckbox: boolean = false,
    colIndexOffset: number = 0,
    isPinned: boolean = false
  ) => {
    const cells = cols.map((col, idx) => {
      const colId = getColId(col);
      const colWidth = resize.getColumnWidth(
        colId,
        getColWidth(col, defaultColumnWidth)
      );
      const colIndex = colIndexOffset + (includeCheckbox ? idx + 1 : idx);

      // Check if this cell has an editable config
      const editConfig = getCellEditConfig
        ? getCellEditConfig(row.id, colId)
        : null;

      const isCellEditing = !!editConfig && editing.isEditing(row.id, colId);

      // When this cell is in edit mode the input takes over tabindex
      const cellTabIndex = isCellEditing
        ? -1
        : keyboard.getCellTabIndex(rowIndex, colIndex);

      let cellContent = editConfig ? (
        <DataTableEditableCell
          rowId={row.id}
          colId={colId}
          editConfig={editConfig}
          editing={editing}
          isCellEditing={isCellEditing}
          cols={cols}
          colIdx={idx}
          rows={rows}
          rowIndex={rowIndex}
          getCellEditConfig={getCellEditConfig}
        />
      ) : (
        (row.cells[colId] ?? null)
      );

      // Left-pane cells omit role='gridcell' — the parent row uses
      // role='presentation' and the right pane carries semantic grid roles.
      const isFirstPinnedCol = isPinned && idx === 0;
      // Same rule as the header: the first pinned column only drops its left
      // padding when a selection checkbox sits beside it.
      const hugsSelectionColumn = isFirstPinnedCol && selectionEnabled;

      // When inlineRowActions is enabled, wrap the first pinned column's content
      // with InlineCellWithActions to show hover actions inline (Attio-style)
      if (
        inlineRowActions &&
        isFirstPinnedCol &&
        getRowContextActions &&
        !editConfig
      ) {
        const contextActions = getRowContextActions(row.id);
        const actions: InlineAction[] = [];

        // Note: Edit is intentionally omitted - clicking the row opens the record
        if (contextActions?.onDuplicate) {
          actions.push({
            key: 'duplicate',
            label: 'Duplicate',
            onAction: contextActions.onDuplicate,
          });
        }
        if (contextActions?.onCopyLink) {
          actions.push({
            key: 'copy-link',
            label: 'Copy link',
            onAction: contextActions.onCopyLink,
          });
        }
        if (contextActions?.extraActions) {
          actions.push(...contextActions.extraActions);
        }
        if (contextActions?.onDelete) {
          actions.push({
            key: 'delete',
            label: 'Delete',
            isDestructive: true,
            onAction: contextActions.onDelete,
          });
        }

        const isHovered = hoveredRowIndex === rowIndex;

        cellContent = (
          <InlineCellWithActions
            actions={actions}
            onOpen={contextActions?.onOpen}
            isHovered={isHovered}
            entityName={row.name}
          >
            {row.cells[colId] ?? null}
          </InlineCellWithActions>
        );
      }

      return (
        <div
          key={colId}
          {...(!isPinned && { role: 'gridcell' as const })}
          // Anchor + focus scope for the Attio-style popped field:
          // EditableCell finds this element via closest() to (a) anchor the
          // selected/editing overlay over the full cell rect and (b) observe
          // focusin/focusout — covering both roving keyboard focus (which
          // lands on this gridcell) and click-focus on the inner cell div.
          {...(editConfig && { 'data-editable-gridcell': '' })}
          ref={el => keyboard.registerCellRef(rowIndex, colIndex, el)}
          style={{ width: colWidth, minWidth: colWidth }}
          className={cx(
            styles.common.cell,
            styles.sizes[size].cell,
            hugsSelectionColumn && 'pl-0'
          )}
          tabIndex={cellTabIndex}
          onKeyDown={e => {
            // While a cell is editing, its input owns the keyboard entirely —
            // grid navigation must not react to bubbled keys (Enter after a
            // commit would activate the row; arrows would yank focus while
            // typing).
            if (isCellEditing) {
              return;
            }
            // On editable cells, Enter/F2 enters edit mode instead of the
            // grid's default Enter-activates-row. EditableCell's inner div
            // handles the same keys with stopPropagation when IT has focus
            // (post-Escape); this branch covers the roving-tabindex case
            // where the gridcell itself is focused.
            if (editConfig && (e.key === 'Enter' || e.key === 'F2')) {
              e.preventDefault();
              editing.startEdit(row.id, colId);
              return;
            }
            // Escape on a selected editable cell exits the selected state:
            // blurring the gridcell closes the popped selection field.
            // (EditableCell's inner div handles the same key with
            // stopPropagation when IT has focus; this covers roving focus.)
            if (editConfig && e.key === 'Escape') {
              e.preventDefault();
              e.currentTarget.blur();
              return;
            }
            keyboard.handleKeyDown(e, rowIndex, colIndex);
          }}
        >
          {cellContent}
        </div>
      );
    });

    if (includeCheckbox) {
      return [renderCheckboxCell(row, rowIndex), ...cells];
    }
    return cells;
  };

  const renderEmptyState = () => {
    if (emptyStateVariant === 'error') {
      return (
        <EmptyStateError
          heading={errorHeading}
          errorMessage={errorMessage}
          onRetry={onRetry}
        />
      );
    }
    if (emptyStateVariant === 'no-results') {
      return <EmptyStateNoResults onClearFilters={onClearFilters} />;
    }
    return (
      <EmptyStateNoData
        entityType={entityType}
        heading={emptyTitle}
        description={emptyDescription}
        onCreateFirst={onCreateFirst}
        createFirstDisabled={createFirstDisabled}
      />
    );
  };

  const renderVirtualRows = (
    cols: DataTableColumnConfig[],
    isPinned: boolean,
    includeCheckbox: boolean = false,
    colIndexOffset: number = 0
  ) =>
    virtualItems.map(virtualRow => {
      const row = rows[virtualRow.index];

      // Render skeleton row for unloaded positions (Attio-style infinite scroll)
      // Note: No measureElement ref for skeletons — they use fixed estimateSize height
      // to avoid layout recalculations and keep scrolling smooth
      if (!row) {
        return (
          <div
            key={`placeholder-${virtualRow.index}`}
            role={isPinned ? 'presentation' : 'row'}
            data-index={virtualRow.index}
            style={{
              position: 'absolute',
              top: 0,
              transform: `translateY(${virtualRow.start}px)`,
              width: '100%',
              height: getEstimatedRowHeight(size),
              // Non-pinned rows need minWidth to support horizontal scrolling
              // Include addColumnExtraWidth so user can scroll to reach the add column button
              ...(isPinned
                ? {}
                : {
                    minWidth:
                      computedRightPaneContentWidth + addColumnExtraWidth,
                  }),
            }}
            className={cx(
              styles.common.row,
              styles.sizes[size].row,
              'pointer-events-none'
            )}
            aria-hidden='true'
          >
            {/* Render the skeleton CELLS directly — this positioned wrapper is
                already the `.row` (border-b + min-height). Nesting a full
                `SkeletonRow` here would double both. */}
            <SkeletonRowCells
              columns={cols}
              size={size}
              defaultColumnWidth={defaultColumnWidth}
              includeCheckbox={includeCheckbox}
              checkboxWidth={checkboxWidth}
              index={virtualRow.index}
              getColumnWidth={resize.getColumnWidth}
            />
          </div>
        );
      }

      const contextActions = getRowContextActions?.(row.id);
      // Only render the context menu button in the right (non-pinned) pane to avoid duplication
      // When inlineRowActions is enabled, don't render the separate context menu column
      const showContextMenu =
        !isPinned && !!contextActions && !inlineRowActions;

      // Only the right (scrollable) pane feeds measurements back to the
      // virtualizer.  Attaching measureElement to both panes causes competing
      // ResizeObserver entries for the same data-index, leading to height
      // thrashing.  The left pane positions rows via virtualRow.start and does
      // not need to measure.
      //
      // Left-pane rows use role='presentation' so screen readers don't
      // report each row twice. The right-pane row carries the semantic
      // ARIA grid structure. Left-pane child cells also omit role='gridcell'.
      const isHovered = hoveredRowIndex === virtualRow.index;

      return (
        <div
          key={row.id}
          role={isPinned ? 'presentation' : 'row'}
          data-index={virtualRow.index}
          data-row-id={isPinned ? undefined : row.rowId}
          ref={isPinned ? undefined : virtualizer.measureElement}
          style={{
            position: 'absolute',
            top: 0,
            transform: `translateY(${virtualRow.start}px)`,
            width: '100%',
            // Pin the left pane row height to the right pane's measured height.
            // Only the right (scrollable) pane feeds measurements to the
            // virtualizer, and consecutive rows are positioned via
            // virtualRow.start (cumulative measured heights).  Without this,
            // a left-pane row with single-line content stays at min-h-[42px]
            // while the right-pane row grows to e.g. 60px for multi-line
            // associations — leaving a vertical gap on the left pane and
            // visually misaligning cells in the same logical row.  The cell's
            // overflow:hidden keeps any rare taller-on-the-left content
            // clipped, consistent with how the right pane handles overflow.
            //
            // Non-pinned rows must NOT pin their height: they are the source
            // of measurement, so locking height: virtualRow.size would freeze
            // the row at its previously-measured size and prevent it from
            // ever growing/shrinking with content changes.
            //
            // Non-pinned rows need minWidth to support horizontal scrolling.
            // Include addColumnExtraWidth so user can scroll to reach the
            // add column button.
            ...(isPinned
              ? { height: virtualRow.size }
              : {
                  minWidth: computedRightPaneContentWidth + addColumnExtraWidth,
                }),
          }}
          className={cx(
            styles.common.row,
            styles.sizes[size].row,
            onRowClick && 'cursor-pointer',
            selectedRowIds.has(row.id) && 'bg-[var(--color-bg-tertiary)]',
            // Use state-based hover instead of CSS hover for synchronized highlighting
            isHovered &&
              !selectedRowIds.has(row.id) &&
              'bg-[var(--color-bg-secondary)]',
            'group/row'
          )}
          onMouseEnter={handleRowMouseEnter(virtualRow.index)}
          onMouseLeave={handleRowMouseLeave}
          onClick={e => {
            // Don't trigger row click if clicking checkbox
            if (!(e.target as HTMLElement).closest('[role="checkbox"]')) {
              onRowClick?.(row.id);
            }
          }}
          {...(!isPinned && {
            'aria-rowindex': virtualRow.index + 2,
            'aria-selected': selectedRowIds.has(row.id),
          })}
        >
          {renderRowCells(
            row,
            cols,
            virtualRow.index,
            includeCheckbox,
            colIndexOffset,
            isPinned
          )}
          {showContextMenu && contextActions && (
            <div
              role='gridcell'
              className={cx(
                'flex items-center justify-center px-1 shrink-0 ml-auto',
                isTouchDevice
                  ? 'opacity-100'
                  : 'opacity-0 group-hover/row:opacity-100',
                'focus-within:opacity-100',
                'transition-opacity duration-75'
              )}
              // Prevent row click from firing when interacting with context menu
              onClick={e => e.stopPropagation()}
            >
              <RowContextMenu
                entityId={row.id}
                entityName={row.name}
                onEdit={contextActions.onEdit}
                onDelete={contextActions.onDelete}
                onDuplicate={contextActions.onDuplicate}
                onCopyLink={contextActions.onCopyLink}
                extraActions={contextActions.extraActions}
              />
            </div>
          )}
        </div>
      );
    });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <DndContext
      sensors={reorder.sensors}
      collisionDetection={reorder.collisionDetection}
      onDragStart={reorder.handleDragStart}
      onDragOver={reorder.handleDragOver}
      onDragMove={reorder.handleDragMove}
      onDragEnd={reorder.handleDragEnd}
    >
      <div
        ref={tableRef}
        role='grid'
        aria-label={ariaLabel}
        aria-rowcount={(totalRowCount ?? rows.length) + 1}
        // Explicitly false for single/none modes per WCAG 2.1 Level A
        aria-multiselectable={selectionMode === 'multiple'}
        className={cx(styles.common.wrapper)}
      >
        {/* aria-live region for state announcements (loading, filtering, sorting) */}
        <div
          role='status'
          aria-live='polite'
          aria-atomic='true'
          className={styles.common.statusRegion}
        >
          {liveAnnouncement}
        </div>

        {/* ── Single scroll container for both header and body ──────────── */}
        {/* Header and body scroll together horizontally — eliminates sync lag */}
        <div
          ref={bodyScrollRef}
          className='flex-1 overflow-auto relative'
          style={{ minHeight: 0 }}
          onScroll={handleBodyScroll}
        >
          {/* Content wrapper — minWidth creates horizontal scroll */}
          <div
            style={{
              minWidth:
                computedLeftPaneWidth +
                computedRightPaneContentWidth +
                addColumnExtraWidth,
              display: 'flex',
              flexDirection: 'column' as const,
              ...(isLoading && { minHeight: '100%' }),
            }}
          >
            {/* ── Header row (sticky top for vertical scroll) ───────────── */}
            <div
              className='flex sticky top-0 z-20'
              style={{ height: headerHeight }}
            >
              {/* Left pane header — sticky left for horizontal scroll.
                  Rendered whenever there are pinned columns / selection,
                  INCLUDING the empty state: the right pane header always
                  renders, so gating the left pane on row presence would drop
                  the pinned first column's header (e.g. an email thread's
                  "Subject") on an empty table, leaving a header row missing
                  its leading column. The body left pane (below) stays gated so
                  there's no frozen empty body pane. */}
              {(leftColumns.length > 0 || selectionEnabled) && (
                <div
                  style={{
                    width: computedLeftPaneWidth,
                    flexShrink: 0,
                    position: 'sticky',
                    left: 0,
                    zIndex: 30,
                  }}
                  className={`${leftColumns.length > 0 ? 'border-r border-[var(--color-border-secondary)] ' : ''}bg-[var(--color-bg-secondary)]`}
                >
                  <div
                    role='row'
                    aria-rowindex={1}
                    className={cx(
                      styles.common.header,
                      styles.sizes[size].header
                    )}
                  >
                    {renderPinnedHeaderCells(leftColumns, selectionEnabled)}
                  </div>
                </div>
              )}

              {/* Right pane header — scrolls with container */}
              <div className='flex-1'>
                <SortableContext
                  items={[
                    DROP_ZONE_START_ID,
                    ...reorder.columnOrder,
                    DROP_ZONE_END_ID,
                  ]}
                  strategy={horizontalListSortingStrategy}
                >
                  <div
                    role='row'
                    aria-rowindex={1}
                    className={cx(
                      styles.common.header,
                      styles.sizes[size].header,
                      'relative' // For absolute positioning of drop indicator
                    )}
                    style={{
                      minWidth:
                        computedRightPaneContentWidth + addColumnExtraWidth,
                    }}
                  >
                    {/* Single drop indicator line — always rendered for fade transition */}
                    <div
                      className='absolute top-0 bottom-0 z-30 pointer-events-none bg-[var(--color-brand-600)]'
                      style={{
                        width: 3,
                        left: dropIndicatorX ?? lastDropIndicatorXRef.current,
                        transform: 'translateX(-50%)',
                        opacity: dropIndicatorX !== null ? 1 : 0,
                        transition: 'opacity 200ms ease-out',
                      }}
                      aria-hidden='true'
                    />
                    {/* Virtual drop zone — allows dragging a column to the leftmost position */}
                    <DropZoneStart />
                    {renderResizableHeaderCells(orderedRightColumns)}
                    {/* Virtual drop zone — allows dragging a column to the rightmost position */}
                    <DropZoneEnd />
                    {/* Add Column header — positioned after last column */}
                    {onAddColumn && availableColumns && (
                      <AddColumnHeader
                        availableColumns={availableColumns}
                        onAddColumn={onAddColumn}
                        onCreateAttribute={onCreateAttribute}
                        size={size}
                      />
                    )}
                  </div>
                </SortableContext>
              </div>
            </div>

            {/* ── Body rows ─────────────────────────────────────────────── */}
            <div
              className='flex'
              style={getBodyContainerStyle(
                isLoading,
                rows.length,
                totalHeight,
                lastBodyHeightRef.current
              )}
            >
              {/* Left pane body (pinned columns) — sticky left for horizontal scroll */}
              {(leftColumns.length > 0 || selectionEnabled) &&
                (isLoading || rows.length > 0) && (
                  <div
                    ref={leftBodyRef}
                    onWheel={handleLeftPaneWheel}
                    style={{
                      width: computedLeftPaneWidth,
                      flexShrink: 0,
                      position: 'sticky',
                      left: 0,
                      overscrollBehaviorX: 'contain',
                    }}
                    className={`${leftColumns.length > 0 ? 'border-r border-[var(--color-border-secondary)] ' : ''}overflow-hidden z-10 bg-[var(--color-bg-primary)]`}
                  >
                    <div
                      className={styles.common.scrollContainer}
                      style={{ height: '100%', position: 'relative' }}
                    >
                      {isLoading ? (
                        <SkeletonRows
                          columns={leftColumns}
                          size={size}
                          defaultColumnWidth={defaultColumnWidth}
                          includeCheckbox={selectionEnabled}
                          checkboxWidth={checkboxWidth}
                          getColumnWidth={resize.getColumnWidth}
                        />
                      ) : (
                        renderVirtualRows(leftColumns, true, selectionEnabled)
                      )}
                    </div>
                  </div>
                )}

              {/* Right pane body — scrolls with container */}
              <div className='flex-1'>
                <div
                  className={styles.common.scrollContainer}
                  style={{ height: '100%', position: 'relative' }}
                >
                  {(() => {
                    if (isLoading) {
                      return (
                        <SkeletonRows
                          columns={orderedRightColumns}
                          size={size}
                          defaultColumnWidth={defaultColumnWidth}
                          getColumnWidth={resize.getColumnWidth}
                        />
                      );
                    }
                    if (rows.length === 0) {
                      // Empty state is rendered as a viewport-centered
                      // overlay on bodyScrollRef (see below the content
                      // wrapper). Returning null here so the right pane
                      // body doesn't constrain the empty state's width
                      // to the column-based content width.
                      return null;
                    }
                    return renderVirtualRows(
                      orderedRightColumns,
                      false,
                      false,
                      leftColumns.length + (selectionEnabled ? 1 : 0)
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* ── Footer row — inside content wrapper so it scrolls
               horizontally with header/body (pure CSS, no JS sync).
               sticky bottom-0 keeps it pinned at the viewport bottom.
               Left pane uses sticky left-0 like the header. ──────────── */}
            {renderFooter && (isLoading || rows.length > 0) && (
              <div
                className={`flex sticky bottom-0 z-20 overflow-clip bg-[var(--color-bg-primary)] -mt-px border-t border-[var(--color-border-secondary)]${hasVerticalOverflow ? '' : ' border-b'}`}
              >
                {(leftColumns.length > 0 || selectionEnabled) && (
                  <div
                    style={{
                      width: computedLeftPaneWidth,
                      flexShrink: 0,
                      position: 'sticky',
                      left: 0,
                      zIndex: 30,
                    }}
                    className='relative bg-[var(--color-bg-primary)]'
                  >
                    {/* Left pane separator — extends below but clipped by footer overflow-clip.
                       Only rendered when there are actual pinned columns (not checkbox-only). */}
                    {leftColumns.length > 0 && (
                      <span
                        aria-hidden='true'
                        className='absolute top-0 right-0 w-px bg-[var(--color-border-secondary)] pointer-events-none'
                        style={{ bottom: '-100vh' }}
                      />
                    )}
                    {renderFooter({
                      pane: 'left',
                      columns,
                      leftColumns,
                      rightColumns: orderedRightColumns,
                      getColumnWidth: resize.getColumnWidth,
                      columnWidths,
                      leftPaneWidth: computedLeftPaneWidth,
                      rightPaneContentWidth: computedRightPaneContentWidth,
                      addColumnExtraWidth,
                      size,
                      selectionEnabled,
                      checkboxWidth,
                    })}
                  </div>
                )}
                <div className='flex-1 overflow-y-visible'>
                  {renderFooter({
                    pane: 'right',
                    columns,
                    leftColumns,
                    rightColumns: orderedRightColumns,
                    getColumnWidth: resize.getColumnWidth,
                    columnWidths,
                    leftPaneWidth: computedLeftPaneWidth,
                    rightPaneContentWidth: computedRightPaneContentWidth,
                    addColumnExtraWidth,
                    size,
                    selectionEnabled,
                    checkboxWidth,
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Empty state overlay — viewport-centered ──────────────
             Positioned absolutely on bodyScrollRef so its width is the
             scroll container's clientWidth (the visible table area),
             NOT the content wrapper's minWidth (the column-based area).
             This keeps the empty state horizontally centered on the
             screen rather than off-center when columns are narrower
             than the viewport. ────────────────────────────────────── */}
          {!isLoading && rows.length === 0 && (
            <div
              className='absolute left-0 right-0 bottom-0 flex items-center justify-center'
              style={{ top: headerHeight }}
            >
              {renderEmptyState()}
            </div>
          )}
        </div>
      </div>

      {/* Floating drag overlay for column being dragged — rendered outside table for proper layering */}
      <DragOverlay dropAnimation={null}>
        {activeColumn && activeId && (
          <ColumnDragOverlay
            label={getColLabel(activeColumn)}
            width={resize.getColumnWidth(
              activeId,
              getColWidth(activeColumn, defaultColumnWidth)
            )}
            size={size}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};
